import { useEffect, useRef } from "react";
import FormatPrice from "./format-price";
import { isMobile } from "react-device-detect";
import { useUI, useProduct, useAppSession, useRouterContext } from "../contexts";
import { FreeMode, A11y, Navigation, Mousewheel } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { isAdoptionListing } from "../data/js/data-sources";
import {
  dasherize,
  googleEvent,
} from "../utils/dom";
import Currency from "./currency";
import Ad from "./ad";
import ListingThumb from "./listing-thumb";
import { AdFramework } from "./ad-framework";
import type { MutableRefObject } from "react";
import type { Swiper as SwiperType } from "swiper";

interface ListingPhoto {
  url?: string;
  "320x320Url"?: string;
  thumbUrl?: string;
}

interface ListingItem {
  id: string | number;
  title: string;
  browse_url?: string;
  price: number;
  cost?: number;
  /** CJ multi-variant range — rendered as `$min – $max` when present. */
  priceMin?: number;
  priceMax?: number;
  photos?: ListingPhoto[];
  hasPhoto?: number;
  locationText?: string;
  dataSourceId?: number;
  status?: number;
  user?: { firstName?: string; trustLevel?: string | number };
  salary_interval?: string;
  itemCondition?: string | number;
  slashedPrice?: string;
  percentOff?: number;
  categoryGroupId?: string | number;
  categoryId?: string | number;
  /**
   * Carried to the card so tag-grain engagement is computable. Without it the
   * tag-level click-through table -- the grain that is actually usable, given
   * per-listing data only covers the head -- would be empty for every showcase
   * row.
   */
  tags?: string[];
  featuredDate?: Date | string | null;
  /** Live listings this seller has of this same item, this one included. */
  copies?: number;
}

const loadingList: ListingItem[] = Array.from({ length: 10 }, (_, i) => ({
  id: `skeleton-${i}`,
  title: "",
  browse_url: "",
  price: 0,
  cost: 0,
}));

const ListingSlider = ({
  filter: _filter = null,
  adsPer = 6,
  adsStart = 6,
  list,
  listName,
  addToList = null,
  swiperRef = null,
  clickable = true,
  tag = null,
  noMargin = false,
  isLoadingMore = false,
  initialSlide = 0,
}: {
  filter?: string | null;
  adsPer?: number;
  adsStart?: number;
  list: ListingItem[];
  listName: string;
  addToList?: ((swiper: SwiperType) => void) | null;
  swiperRef?: MutableRefObject<{ swiper: SwiperType } | null> | null;
  clickable?: boolean;
  tag?: string | null;
  noMargin?: boolean;
  isLoadingMore?: boolean;
  /** Where the row opens. A windowed showcase passes the slide it was on when it was parked. */
  initialSlide?: number;
}) => {
  const { setProductOpen } = useUI();
  const { setProduct, productDetail } = useProduct();
  const { clientSide } = useAppSession();
  const { push } = useRouterContext();

  // Back to the first card when the open product changes -- but not on mount.
  // Firing on mount threw away `initialSlide`, so a windowed row that came
  // back on screen always opened at its first card, not where it was left.
  const productId = productDetail?.id;
  const lastProductIdRef = useRef(productId);
  useEffect(() => {
    if (lastProductIdRef.current === productId) return;
    lastProductIdRef.current = productId;
    swiperRef?.current?.swiper?.slideTo(0);
  }, [productId, swiperRef]);

  return (
    <Swiper
      ref={swiperRef}
      className="listing-slider"
      watchSlidesProgress
      modules={[FreeMode, A11y, Navigation, Mousewheel]}
      freeMode
      navigation
      // A sideways trackpad swipe moves the row on desktop, where drag is off.
      // forceToAxis ignores vertical wheel so the page still scrolls past it.
      mousewheel={{ forceToAxis: true, releaseOnEdges: true }}
      lazy={{
        loadPrevNext: true,
      }}
      slidesPerView={"auto"}
      initialSlide={initialSlide}
      threshold={10}
      // preloadImages={true}
      preventClicks={true}
      spaceBetween={10}
      slidesOffsetBefore={noMargin ? 0 : 25}
      direction="horizontal"
      onReachEnd={(s) => {
        if (addToList) addToList(s);
      }}
      breakpoints={{
        821: {
          allowTouchMove: false,
          navigation: {
            enabled: true,
          },
        },
      }}
      onSlideChange={() => {
        googleEvent("swipe_listings");
      }}
    >
      {(list?.length ? list : loadingList)?.map((item, index) => {
        const {
          id,
          photos,
          hasPhoto,
          title,
          price,
          priceMin,
          priceMax,
          locationText,
          dataSourceId,
          status,
          user = {},
          browse_url,
          salary_interval,
          itemCondition,
        } = item;

        const isJob = item.browse_url && item.browse_url.match(/ziprecruiter/);
        const onClick = (e: React.MouseEvent) => {
          if (!isJob && clickable) {
            e.preventDefault();
            const productUrl = `/product/${id}/${dasherize(title) || "untitled"}/`;
            push(productUrl);
            setProduct(item as Parameters<typeof setProduct>[0]);
            setProductOpen(true);
          }
        };

        const trustLevel = parseInt(user.trustLevel as string);

        // An item with no id renders as a skeleton below and never uses this.
        const url = isJob ? browse_url : `/product/${id}/${dasherize(title)}`;

        let amount: React.ReactNode;
        if (isJob) {
          if (price > 0) {
            amount = (
              <div>
                {Currency(price, FormatPrice)} {salary_interval}
              </div>
            );
          } else {
            amount = "";
          }
        } else {
          if (
            priceMin !== undefined &&
            priceMax !== undefined &&
            priceMin < priceMax
          ) {
            // CJ multi-variant range
            amount = (
              <div data-testid="listing-price-range">
                {Currency(priceMin, FormatPrice)} – {Currency(priceMax, FormatPrice)}
              </div>
            );
          } else if (price > 0) {
            amount = <div>{Currency(price, FormatPrice)}</div>;
          } else {
            amount = isAdoptionListing(dataSourceId) ? (
              <div className="location-text">{locationText}</div>
            ) : (
              ""
            );
          }
        }

        const photo = getMainImage(item);

        const outgoing = isJob
          ? {
              target: "_blank",
              rel: "noreferrer noopener",
            }
          : {
              onClick,
            };

        const isSkeleton = typeof id === 'string' && id.startsWith('skeleton-');
        if (!id || isSkeleton) {
          return (
            <SwiperSlide className="product-list-item" key={`placeholder-${index}`}>
              <div className="listing">
                <div className="listing-image">
                  <div className="listing-image-cont skeleton" />
                </div>
                <div className="listing-price">&nbsp;</div>
              </div>
            </SwiperSlide>
          );
        }

        if (
          clientSide &&
          index >= adsStart &&
          (index - adsStart) % adsPer === 0
        ) {
          return [
            <SwiperSlide className="product-list-item-ad" key={item.id}>
              <AdFramework
                bidstream={{
                  mobile: (
                    <div
                      data-ad-tagid="wgw8vFZoalasLTuQSL4Q"
                      data-zid="1018"
                      data-name="SW - sellwild.com mobile 300x250"
                    />
                  ),
                  desktop: (
                    <div
                      data-ad-tagid="R7wZB7IZrKPO5EJAZTjq"
                      data-zid="1015"
                      data-name="SW - sellwild.com desktop 300x250"
                    />
                  ),
                }}
                adsense={
                  <Ad
                    key={item.id + "-ad"}
                    className="adsbygoogle"
                    style={{ display: "block", margin: "auto", width: 300, height: 250 }}
                    data-ad-client="ca-pub-3154552656014103"
                    data-ad-slot="9551701632"
                  />
                }
              />
            </SwiperSlide>,
            <SwiperSlide className="product-list-item" key={`${id}-listing`}>
              <ListingThumb
                title={title}
                url={url}
                outgoing={outgoing}
                trustLevel={trustLevel}
                amount={amount}
                status={status}
                photo={photo}
                photos={photos}
                hasPhoto={hasPhoto}
                id={id}
                isJob={false}
                local={false}
                slashedPrice={item.slashedPrice}
                clickable={clickable}
                percentOff={item.percentOff}
                itemCondition={itemCondition}
                dataSourceId={dataSourceId}
                tag={tag}
                categoryGroupId={item.categoryGroupId}
                categoryId={item.categoryId}
                tags={item.tags}
                listName={listName}
                resultPosition={index + 1}
                totalResults={list.length}
                featuredDate={item.featuredDate}
                copies={item.copies}
                locationText={locationText}
              />
            </SwiperSlide>,
          ];
        }

        return (
          <SwiperSlide className="product-list-item" key={id}>
            <ListingThumb
              title={title}
              url={url}
              outgoing={outgoing}
              trustLevel={trustLevel}
              amount={amount}
              status={status}
              photo={photo}
              photos={photos}
              hasPhoto={hasPhoto}
              id={id}
              isJob={false}
              local={false}
              slashedPrice={item.slashedPrice}
              clickable={clickable}
              percentOff={item.percentOff}
              itemCondition={itemCondition}
              dataSourceId={dataSourceId}
              tag={tag}
              categoryGroupId={item.categoryGroupId}
              categoryId={item.categoryId}
              tags={item.tags}
              listName={listName}
              resultPosition={index + 1}
              totalResults={list.length}
              featuredDate={item.featuredDate}
              copies={item.copies}
              locationText={locationText}
            />
          </SwiperSlide>
        );
      })}
      {isLoadingMore && (
        <SwiperSlide className="product-list-item" key="loading-more">
          <div className="listing">
            <div className="listing-image">
              <div className="listing-image-cont skeleton" />
            </div>
            <div className="listing-price">&nbsp;</div>
          </div>
        </SwiperSlide>
      )}
    </Swiper>
  );
};

/**
 * Slider tiles are thumbnail-sized, so take the 320px variant rather than the
 * 1280x1280 original. Foreign-hosted photos have no variants and fall through
 * to `url` unchanged.
 */
const getMainImage = ({ photos }: { photos?: ListingPhoto[] }) => {
  const first = (photos || [])[0];
  if (!first) return "";
  return first["320x320Url"] || first.thumbUrl || first.url;
};

export default ListingSlider;
